import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import * as React from "react"
import { toast } from "sonner"
import { z } from "zod"
import {
	declarationStatusPresentation,
	FormRow,
	ID_LINK_CLASS,
	Money,
	QueryErrorLine,
	SkeletonRows,
	StatusView,
} from "@/components/accounting/accounting-ui"
import { PageHeader, Panel, Section, Toolbar } from "@/components/layout"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import {
	type DeclarableScheme,
	useBuildFirstReturn,
	useVatDeclarations,
	useVatProvisionSummary,
	type VatDeclarationStatus,
} from "@/queries/marketplace/use-vat-declarations"

const SCHEMES: DeclarableScheme[] = [
	"eu_oss",
	"ioss",
	"fr_domestic",
	"uk_vat",
	"ch_vat",
]
const STATUSES: VatDeclarationStatus[] = [
	"draft",
	"provisioned",
	"filed",
	"paid",
	"superseded",
]

const searchSchema = z.object({
	scheme: z
		.enum(["eu_oss", "ioss", "fr_domestic", "uk_vat", "ch_vat"])
		.optional(),
	year: z.coerce.number().int().min(2020).max(2100).optional().catch(undefined),
	status: z
		.enum(["draft", "provisioned", "filed", "paid", "superseded"])
		.optional(),
})

export const Route = createFileRoute("/accounting/vat-declarations/")({
	component: VatDeclarationsPage,
	validateSearch: (search: Record<string, unknown>) =>
		searchSchema.parse(search),
})

function VatDeclarationsPage() {
	const search = useSearch({ from: "/accounting/vat-declarations/" })
	const navigate = useNavigate()
	const declarations = useVatDeclarations(search)
	const provisionSummary = useVatProvisionSummary()

	const items = declarations.data?.items ?? []

	return (
		<div className="flex flex-col gap-6 p-6">
			<PageHeader
				title="VAT declarations"
				count={declarations.data ? items.length : undefined}
				description="What to declare and pay per scheme, filing period and country: OSS, IOSS, the French return, and UK and Swiss VAT provisions."
			/>

			<Toolbar>
				<Select
					value={search.scheme ?? "all"}
					onValueChange={(v) =>
						navigate({
							to: "/accounting/vat-declarations",
							search: {
								...search,
								scheme: v === "all" ? undefined : (v as DeclarableScheme),
							},
						})
					}
				>
					<SelectTrigger aria-label="Scheme" className="w-40">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All schemes</SelectItem>
						{SCHEMES.map((s) => (
							<SelectItem key={s} value={s}>
								{s}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Select
					value={search.status ?? "all"}
					onValueChange={(v) =>
						navigate({
							to: "/accounting/vat-declarations",
							search: {
								...search,
								status: v === "all" ? undefined : (v as VatDeclarationStatus),
							},
						})
					}
				>
					<SelectTrigger aria-label="Status" className="w-40">
						<SelectValue />
					</SelectTrigger>
					<SelectContent>
						<SelectItem value="all">All statuses</SelectItem>
						{STATUSES.map((s) => (
							<SelectItem key={s} value={s}>
								{declarationStatusPresentation(s, false).label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
				<Input
					type="number"
					aria-label="Year"
					placeholder="Year"
					className="w-24"
					value={search.year ?? ""}
					onChange={(e) =>
						navigate({
							to: "/accounting/vat-declarations",
							search: {
								...search,
								year: e.target.value ? Number(e.target.value) : undefined,
							},
						})
					}
				/>
			</Toolbar>

			{declarations.isError ? (
				<QueryErrorLine what="the declarations" error={declarations.error} />
			) : (
				<Panel>
					{declarations.isLoading ? (
						<SkeletonRows rows={6} />
					) : (
						<Table>
							<TableHeader>
								<TableRow>
									<TableHead>Scheme</TableHead>
									<TableHead>Period</TableHead>
									<TableHead>Due date</TableHead>
									<TableHead>Status</TableHead>
									<TableHead className="text-right">Total due (EUR)</TableHead>
									<TableHead className="text-right">Paid (EUR)</TableHead>
									<TableHead className="text-right">
										Outstanding (EUR)
									</TableHead>
								</TableRow>
							</TableHeader>
							<TableBody>
								{items.length === 0 && (
									<TableRow className="hover:bg-transparent">
										<TableCell colSpan={7} className="text-muted-foreground">
											No declarations for these filters.
										</TableCell>
									</TableRow>
								)}
								{items.map((d) => (
									<TableRow
										key={d.declarationId}
										className="cursor-pointer"
										onClick={() =>
											navigate({
												to: "/accounting/vat-declarations/$declarationId",
												params: { declarationId: d.declarationId },
											})
										}
									>
										<TableCell>
											<span className="flex items-center gap-2">
												<span className={ID_LINK_CLASS}>{d.scheme}</span>
												{d.kind !== "regular" && (
													<Badge variant="neutral">
														{d.kind === "first_return"
															? "First return"
															: "Imported"}
													</Badge>
												)}
											</span>
										</TableCell>
										<TableCell className="font-mono">
											{d.filingPeriod}
										</TableCell>
										<TableCell className="tabular-nums text-muted-foreground">
											{new Date(d.dueDate).toLocaleDateString()}
										</TableCell>
										<TableCell>
											<StatusView
												presentation={declarationStatusPresentation(
													d.status,
													d.overdue,
												)}
											/>
										</TableCell>
										<TableCell className="text-right">
											<Money cents={d.totalDueEur} currency="EUR" />
										</TableCell>
										<TableCell className="text-right text-muted-foreground">
											<Money cents={d.paidEur} currency="EUR" />
										</TableCell>
										<TableCell className="text-right font-medium">
											<Money cents={d.outstandingEur} currency="EUR" muteZero />
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					)}
				</Panel>
			)}

			{(provisionSummary.data?.schemes.length ?? 0) > 0 && (
				<div className="mt-2 grid grid-cols-1 gap-8 xl:grid-cols-2">
					{provisionSummary.data?.schemes.map((scheme) => (
						<ProvisionSection key={scheme.scheme} scheme={scheme} />
					))}
				</div>
			)}
		</div>
	)
}

function ProvisionSection({
	scheme,
}: {
	scheme: {
		scheme: "uk_vat" | "ch_vat"
		currency: string
		periods: Array<{
			filingPeriod: string
			vatLocal: number
			vatEur: number
			status: VatDeclarationStatus
		}>
		cumulativeLocal: number
		cumulativeEur: number
	}
}) {
	const buildFirstReturn = useBuildFirstReturn()
	const [open, setOpen] = React.useState(false)
	const [effectiveFrom, setEffectiveFrom] = React.useState("")
	const [firstPeriodEnd, setFirstPeriodEnd] = React.useState("")
	const [registrationNumber, setRegistrationNumber] = React.useState("")

	const handleBuild = async () => {
		if (!effectiveFrom || !firstPeriodEnd || !registrationNumber.trim()) {
			toast.error("All fields are required.")
			return
		}
		try {
			await buildFirstReturn.mutateAsync({
				scheme: scheme.scheme,
				effectiveFrom: new Date(effectiveFrom),
				firstPeriodEnd: new Date(firstPeriodEnd),
				registrationNumber: registrationNumber.trim(),
			})
			toast.success(
				"First return built. The provisioned periods it covers are now superseded.",
			)
			setOpen(false)
		} catch (e) {
			const code = (e as { code?: string }).code
			toast.error(`Could not build the first return${code ? `: ${code}` : "."}`)
		}
	}

	const name = scheme.scheme === "uk_vat" ? "UK VAT" : "Swiss VAT"
	const idPrefix = `first-return-${scheme.scheme}`

	return (
		<Section
			label={`${name} · provisioned, registration pending`}
			action={
				!open ? (
					<Button variant="ghost" size="sm" onClick={() => setOpen(true)}>
						Build first return
					</Button>
				) : undefined
			}
		>
			<Panel>
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>Period</TableHead>
							<TableHead className="text-right">{scheme.currency}</TableHead>
							<TableHead className="text-right">EUR</TableHead>
							<TableHead>Status</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{scheme.periods.map((p) => (
							<TableRow key={p.filingPeriod}>
								<TableCell className="font-mono">{p.filingPeriod}</TableCell>
								<TableCell className="text-right">
									<Money cents={p.vatLocal} currency={scheme.currency} />
								</TableCell>
								<TableCell className="text-right">
									<Money cents={p.vatEur} currency="EUR" />
								</TableCell>
								<TableCell>
									<StatusView
										presentation={declarationStatusPresentation(
											p.status,
											false,
										)}
									/>
								</TableCell>
							</TableRow>
						))}
						<TableRow className="font-semibold hover:bg-transparent">
							<TableCell>Cumulative</TableCell>
							<TableCell className="text-right">
								<Money
									cents={scheme.cumulativeLocal}
									currency={scheme.currency}
								/>
							</TableCell>
							<TableCell className="text-right">
								<Money cents={scheme.cumulativeEur} currency="EUR" />
							</TableCell>
							<TableCell />
						</TableRow>
					</TableBody>
				</Table>
			</Panel>
			{open && (
				<div className="flex flex-col">
					<FormRow label="Effective from" htmlFor={`${idPrefix}-from`}>
						<Input
							id={`${idPrefix}-from`}
							type="date"
							value={effectiveFrom}
							onChange={(e) => setEffectiveFrom(e.target.value)}
						/>
					</FormRow>
					<FormRow label="First period end" htmlFor={`${idPrefix}-end`}>
						<Input
							id={`${idPrefix}-end`}
							type="date"
							value={firstPeriodEnd}
							onChange={(e) => setFirstPeriodEnd(e.target.value)}
						/>
					</FormRow>
					<FormRow label="Registration number" htmlFor={`${idPrefix}-reg`}>
						<Input
							id={`${idPrefix}-reg`}
							value={registrationNumber}
							onChange={(e) => setRegistrationNumber(e.target.value)}
							placeholder={
								scheme.scheme === "uk_vat" ? "GB123456789" : "CHE-123.456.789"
							}
						/>
					</FormRow>
					<div className="flex justify-end gap-2 pt-4">
						<Button variant="ghost" onClick={() => setOpen(false)}>
							Cancel
						</Button>
						<Button onClick={handleBuild} disabled={buildFirstReturn.isPending}>
							Build
						</Button>
					</div>
				</div>
			)}
		</Section>
	)
}
